/**
 * OnboardingWizard — guided 10-step setup for new operators.
 *
 * Operator SaaS launch-readiness. Surfaces every organisational setting an
 * operator needs to "go live" without technical help. Reads + writes a single
 * document via /admin/operator-settings; auto-derives progress from the
 * persisted fields AND the live drivers/passengers counts.
 *
 * Strict isolation contract:
 *   - Pure organisational / settings UI. No dispatch / state-machine / janitor /
 *     offer-routing / ride-lifecycle / pricing-engine / payments / driver-
 *     assignment code is touched.
 *   - The canonical fare/payment config lives on the existing Settings tab; the
 *     wizard only records a "reviewed" snapshot and links across.
 *   - All persistence is namespaced under `operator_settings` (founding → the
 *     legacy "default" doc; every other tenant → its own operator_id doc), so a
 *     new operator always starts with an EMPTY isolated workspace.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { invalidateBrandingCache } from "../../hooks/useBranding";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Building2, MapPin, FileText, Settings as SettingsIcon, Users, UserCheck,
  Palette, DollarSign, CheckCircle2, Circle, ChevronRight, Save,
  Rocket, Sparkles, Loader2, RefreshCw, KeyRound, ShieldCheck, AlertCircle,
  Phone, CreditCard, ClipboardCheck,
} from "lucide-react";
import {
  COUNTRIES, LANGUAGES, ADDRESSING_MODES, CURRENCIES,
  TIME_FORMATS, DATE_FORMATS, TIMEZONES, findCountry,
} from "../../lib/i18nCatalog";

const STEP_META = [
  { id: "profile",  label: "Company profile",  icon: Building2 },
  { id: "branding", label: "Branding",         icon: Palette },
  { id: "booking",  label: "Booking settings", icon: Phone },
  { id: "area",     label: "Operating area",   icon: MapPin },
  { id: "pricing",  label: "Pricing",          icon: DollarSign },
  { id: "drivers",  label: "Drivers",          icon: Users },
  { id: "payments", label: "Payments",         icon: CreditCard },
  { id: "ai",       label: "AI settings",      icon: Sparkles },
  { id: "test",     label: "Test booking",     icon: ClipboardCheck },
  { id: "golive",   label: "Go-live checklist",icon: Rocket },
];

// ─────────────────────────────────────────────────────────────────────────
// Small primitives — kept inline so this slice ships as one file.
// ─────────────────────────────────────────────────────────────────────────

const Field = ({ label, hint, children, testid }) => (
  <label className="block" data-testid={testid ? `field-${testid}` : undefined}>
    <Label className="label-mini text-muted-foreground mb-1 block">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </label>
);

const StepBadge = ({ progress }) => {
  if (!progress) return null;
  const cls = progress.done
    ? "bg-[#ECFDF5] text-[#065F46] border-[#10B981]"
    : "bg-secondary text-muted-foreground border-border";
  const label = progress.done ? "DONE" : "TO DO";
  return (
    <span className={`label-mini inline-flex items-center gap-1 px-1.5 py-0.5 border ${cls}`}>
      {progress.done ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
      {label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────

export const OnboardingWizard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState("profile");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/operator-settings");
      setData(res.data);
      setForm(res.data.settings || {});
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load onboarding state");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.settings) setForm((f) => ({ ...data.settings, ...f }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.settings?.updated_at]);

  const onField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const saveFields = async (fields) => {
    setBusy(true);
    try {
      const res = await api.put("/admin/operator-settings", fields);
      setData(res.data);
      invalidateBrandingCache();
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleStepComplete = async (stepId, next) => {
    setBusy(true);
    try {
      const res = await api.post("/admin/operator-settings/complete-step", {
        step_id: stepId, completed: next,
      });
      setData(res.data);
      toast.success(next ? "Step marked complete" : "Step unmarked");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not update step");
    } finally {
      setBusy(false);
    }
  };

  const confirmGoLive = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/operator-settings", { go_live_confirmed: true });
      setData(res.data);
      toast.success("You're live", {
        description: "Your operator workspace is officially open. Welcome aboard.",
      });
    } catch (e) {
      toast.error(formatApiError(e) || "Could not confirm");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center justify-center text-sm text-muted-foreground" data-testid="onboarding-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading onboarding state…
      </div>
    );
  }

  const { progress, counts, summary } = data;
  const stepProgress = progress[activeStep];

  return (
    <div className="bg-white" data-testid="onboarding-wizard">
      {/* PROGRESS HEADER ────────────────────────────────────────────── */}
      <div className="px-4 md:px-6 py-4 border-b border-border bg-[#FAFAF7]">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <h2 className="font-heading text-2xl font-black tracking-tight" data-testid="onboarding-title">
              {summary.percent === 100
                ? "You're ready to launch"
                : summary.percent === 0
                  ? "Welcome — let's set up your operator"
                  : "Let's finish setting you up"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {summary.percent === 100
                ? "Every essential is in place. Confirm go-live to officially open for business — you can keep editing settings any time."
                : summary.percent === 0
                  ? "Ten quick steps to get your fleet ready. Takes about 15 minutes; each step auto-saves so you can stop and pick up later."
                  : `${summary.steps_total - summary.steps_done} step${summary.steps_total - summary.steps_done === 1 ? "" : "s"} to go. Each one auto-saves so you can stop and pick up later.`}
            </p>
          </div>
          <Button
            variant="ghost" size="sm" onClick={load} disabled={busy}
            data-testid="onboarding-refresh"
            title="Refresh from server"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-secondary border border-border overflow-hidden" data-testid="onboarding-progress-bar">
            <div
              className={`h-full transition-all ${summary.percent === 100 ? "bg-[#10B981]" : "bg-[#0A0A0A]"}`}
              style={{ width: `${summary.percent}%` }}
              data-testid="onboarding-progress-fill"
            />
          </div>
          <div className="label-mini text-muted-foreground whitespace-nowrap font-mono-tab" data-testid="onboarding-progress-label">
            {summary.percent === 100 && !summary.go_live_confirmed
              ? <span className="text-[#10B981] font-bold">READY TO LAUNCH</span>
              : <>{summary.steps_done} / {summary.steps_total} · {summary.percent}%</>}
          </div>
        </div>

        {summary.go_live_confirmed && (
          <div
            className="mt-3 border border-[#10B981] bg-[#ECFDF5] text-[#065F46] px-3 py-2 text-xs flex items-start gap-2"
            data-testid="onboarding-go-live-banner"
          >
            <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>You're live.</strong> Your operator workspace is officially open for business.
              Keep an eye on the <strong>Ride Health</strong> tab for live operational alerts,
              and revisit Setup any time to update branding, fares, or contact details.
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
        {/* STEPPER SIDEBAR ──────────────────────────────────────────── */}
        <nav className="border-r border-border bg-white" data-testid="onboarding-stepper">
          {STEP_META.map((s, idx) => {
            const p = progress[s.id];
            const active = activeStep === s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveStep(s.id)}
                className={`w-full text-left px-4 py-3 border-b border-border flex items-center gap-2 transition-colors ${active ? "bg-[#FFFBEB] border-l-4 border-l-[#FFC107]" : "hover:bg-secondary border-l-4 border-l-transparent"}`}
                data-testid={`step-tab-${s.id}`}
              >
                <span className="font-mono-tab text-[11px] text-muted-foreground w-4">{idx + 1}</span>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-sm font-semibold truncate">{s.label}</span>
                {p?.done && (
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" data-testid={`step-done-${s.id}`} />
                )}
              </button>
            );
          })}
          <div className="px-4 py-3">
            <Button
              onClick={confirmGoLive}
              disabled={busy || summary.percent < 100 || summary.go_live_confirmed}
              className="w-full bg-[#0A0A0A] hover:bg-black text-white text-xs font-bold flex items-center justify-center gap-2"
              data-testid="onboarding-confirm-go-live"
            >
              <Rocket className="w-3.5 h-3.5" />
              {summary.go_live_confirmed
                ? (summary.percent < 100 ? "Live · needs attention" : "Live")
                : summary.percent === 100
                  ? "Go live"
                  : `${summary.percent}% — keep going`}
            </Button>
            {summary.percent < 100 && !summary.go_live_confirmed && (
              <div className="text-[10px] text-muted-foreground mt-1 text-center">
                Finish every step to unlock go-live.
              </div>
            )}
            {summary.go_live_confirmed && summary.percent < 100 && (
              <div
                className="text-[10px] text-[#92400E] mt-1 text-center"
                data-testid="onboarding-live-needs-attention"
              >
                A step changed — review and re-save to restore 100%.
              </div>
            )}
          </div>
        </nav>

        {/* ACTIVE STEP PANEL ───────────────────────────────────────── */}
        <section className="p-4 md:p-6 min-h-[400px]" data-testid={`step-panel-${activeStep}`}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="label-mini text-muted-foreground">
                STEP {STEP_META.findIndex((s) => s.id === activeStep) + 1} OF {STEP_META.length}
              </div>
              <h3 className="font-heading text-xl font-black mt-0.5">
                {STEP_META.find((s) => s.id === activeStep)?.label}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <StepBadge progress={stepProgress} />
              {stepProgress && !stepProgress.auto && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => toggleStepComplete(activeStep, !stepProgress.manual)}
                  disabled={busy}
                  className="text-xs"
                  data-testid={`step-toggle-complete-${activeStep}`}
                >
                  {stepProgress.manual ? "Untick" : "Mark done"}
                </Button>
              )}
            </div>
          </div>

          {activeStep === "profile"  && <ProfileStep  form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "branding" && <BrandingStep form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "booking"  && <BookingStep  form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "area"     && <AreaStep     form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "pricing"  && <PricingStep  form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "drivers"  && <DriversStep  counts={counts} progress={progress} />}
          {activeStep === "payments" && <PaymentsStep form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "ai"       && <AiStep       form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "test"     && <TestBookingStep form={form} onField={onField} onSave={saveFields} busy={busy} />}
          {activeStep === "golive"   && <GoLiveStep   summary={summary} progress={progress} onGoLive={confirmGoLive} busy={busy} />}

          {/* Navigation footer */}
          <div className="mt-6 pt-4 border-t border-border flex items-center justify-between gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => {
                const idx = STEP_META.findIndex((s) => s.id === activeStep);
                if (idx > 0) setActiveStep(STEP_META[idx - 1].id);
              }}
              disabled={STEP_META[0].id === activeStep}
              data-testid="onboarding-prev"
            >
              Previous
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const idx = STEP_META.findIndex((s) => s.id === activeStep);
                if (idx < STEP_META.length - 1) setActiveStep(STEP_META[idx + 1].id);
              }}
              disabled={STEP_META[STEP_META.length - 1].id === activeStep}
              className="bg-[#0A0A0A] hover:bg-black text-white flex items-center gap-1.5"
              data-testid="onboarding-next"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </section>
      </div>

      <ChangePasswordPanel />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// SECURITY — Change password panel
// ─────────────────────────────────────────────────────────────────────────

const MIN_PW_LEN = 8;

const ChangePasswordPanel = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!current) {
      setError("Enter your current password.");
      return;
    }
    if (next.length < MIN_PW_LEN) {
      setError(`New password must be at least ${MIN_PW_LEN} characters.`);
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (next === current) {
      setError("New password must be different from your current password.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/admin/change-password", {
        current_password: current,
        new_password: next,
      });
      reset();
      setSuccess(
        "Password updated. Verify it in a private/incognito window before logging out of this session."
      );
      toast.success("Password changed");
    } catch (err) {
      setError(formatApiError(err) || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  const pwInput = "w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]";

  return (
    <div className="px-4 md:px-6 py-6 border-t border-border bg-[#FAFAF7]" data-testid="change-password-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-[#0A0A0A]" />
        <h3 className="font-heading text-lg font-black tracking-tight">Security · Change password</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4 max-w-xl">
        Update your admin sign-in password. Your current session stays active — confirm the
        new password in a separate incognito window before logging out.
      </p>

      <form onSubmit={submit} className="max-w-md space-y-3" data-testid="change-password-form" autoComplete="off">
        <label className="block">
          <Label className="label-mini text-muted-foreground mb-1 block">Current password</Label>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={pwInput}
            autoComplete="current-password"
            data-testid="change-password-current-input"
          />
        </label>
        <label className="block">
          <Label className="label-mini text-muted-foreground mb-1 block">New password</Label>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={pwInput}
            autoComplete="new-password"
            data-testid="change-password-new-input"
          />
          <div className="text-[11px] text-muted-foreground mt-1">At least {MIN_PW_LEN} characters.</div>
        </label>
        <label className="block">
          <Label className="label-mini text-muted-foreground mb-1 block">Confirm new password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={pwInput}
            autoComplete="new-password"
            data-testid="change-password-confirm-input"
          />
        </label>

        {error && (
          <div
            className="flex items-start gap-2 border border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B] px-3 py-2 text-xs"
            data-testid="change-password-error"
          >
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div
            className="flex items-start gap-2 border border-[#10B981] bg-[#ECFDF5] text-[#065F46] px-3 py-2 text-xs"
            data-testid="change-password-success"
          >
            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={busy}
          className="bg-[#0A0A0A] hover:bg-black text-white text-xs font-bold flex items-center gap-2"
          data-testid="change-password-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// STEP COMPONENTS
// ─────────────────────────────────────────────────────────────────────────

const inputCls = "w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]";

const SaveBar = ({ onSave, busy, testid = "step-save", label = "Save changes" }) => (
  <div className="mt-4">
    <Button
      onClick={onSave} disabled={busy}
      className="bg-[#0A0A0A] hover:bg-black text-white text-xs font-bold flex items-center gap-1.5"
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
      {label}
    </Button>
  </div>
);

// 1 — Company profile (name · licence area · contact)
const ProfileStep = ({ form, onField, onSave, busy }) => (
  <div className="space-y-3" data-testid="profile-step">
    <p className="text-sm text-muted-foreground">
      The basics. Customers see your <strong>company name</strong> on every receipt and
      transactional email, and your <strong>licence details</strong> reassure them you're a
      properly licensed private-hire operator.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="COMPANY NAME" testid="company-name" hint="As it appears on signage, ads, and your van livery.">
        <Input value={form.company_name || ""} onChange={onField("company_name")} placeholder="e.g. Crystal Cabs Ltd" data-testid="input-company-name" className={inputCls} />
      </Field>
      <Field label="LEGAL ENTITY (optional)" testid="legal-name" hint="Only if different from your trading name — used on invoices.">
        <Input value={form.legal_name || ""} onChange={onField("legal_name")} placeholder="e.g. Crystal Workforce Limited" data-testid="input-legal-name" className={inputCls} />
      </Field>
      <Field label="LICENSING AUTHORITY" testid="licensing-authority" hint="The council or borough that issued your operator licence.">
        <Input value={form.licensing_authority || ""} onChange={onField("licensing_authority")} placeholder="e.g. Basingstoke & Deane Borough Council" data-testid="input-licensing-authority" className={inputCls} />
      </Field>
      <Field label="LICENCE NUMBER" testid="licence-number" hint="The full reference shown on your licence document.">
        <Input value={form.licence_number || ""} onChange={onField("licence_number")} placeholder="e.g. PHO/00/12345" data-testid="input-licence-number" className={inputCls} />
      </Field>
      <Field label="LICENCE EXPIRY (optional)" testid="licence-expiry" hint="We'll flag expiring licences so you can renew in good time.">
        <Input type="date" value={form.licence_expiry || ""} onChange={onField("licence_expiry")} data-testid="input-licence-expiry" className={inputCls} />
      </Field>
      <Field label="MAIN CONTACT EMAIL" testid="company-email" hint="Where booking confirmations and operational replies should go.">
        <Input type="email" value={form.company_email || ""} onChange={onField("company_email")} placeholder="hello@yourcompany.example" data-testid="input-company-email" className={inputCls} />
      </Field>
      <Field label="MAIN CONTACT PHONE" testid="company-phone" hint="Best number to reach you in office hours.">
        <Input value={form.company_phone || ""} onChange={onField("company_phone")} placeholder="+44 1256 000 000" data-testid="input-company-phone" className={inputCls} />
      </Field>
      <Field label="WEBSITE (optional)" testid="website">
        <Input value={form.website || ""} onChange={onField("website")} placeholder="https://yourcompany.example" data-testid="input-website" className={inputCls} />
      </Field>
    </div>
    <SaveBar busy={busy} testid="profile-save" label="Save profile" onSave={() => onSave({
      company_name: form.company_name, legal_name: form.legal_name,
      licensing_authority: form.licensing_authority, licence_number: form.licence_number,
      licence_expiry: form.licence_expiry,
      company_email: form.company_email, company_phone: form.company_phone,
      website: form.website,
    })} />
  </div>
);

// 2 — Branding (logo · colours · public display name)
const BrandingStep = ({ form, onField, onSave, busy }) => (
  <div className="space-y-3" data-testid="branding-step">
    <p className="text-sm text-muted-foreground">
      Make the platform feel like <em>your</em> brand. Your <strong>public display name</strong>,
      logo, and colours appear across emails, receipts, and your public landing page. None of
      these affect dispatching.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="PUBLIC DISPLAY NAME" testid="public-display-name" hint="The customer-facing name shown on your landing page + emails.">
        <Input value={form.public_display_name || ""} onChange={onField("public_display_name")} placeholder="e.g. Crystal Cabs" data-testid="input-public-display-name" className={inputCls} />
      </Field>
      <Field label="LOGO URL (optional)" testid="brand-logo" hint="Hosted PNG / SVG — shows in transactional emails and dashboards.">
        <Input value={form.brand_logo_url || ""} onChange={onField("brand_logo_url")} placeholder="https://cdn.example/logo.png" data-testid="input-brand-logo" className={inputCls} />
      </Field>
      <Field label="PRIMARY COLOUR (optional)" testid="brand-primary" hint="Hex value, e.g. #0A0A0A — used for buttons + accents.">
        <div className="flex items-center gap-2">
          <input type="color" value={form.brand_primary_color || "#0A0A0A"} onChange={onField("brand_primary_color")} data-testid="input-brand-primary-color" className="h-9 w-12 border border-border cursor-pointer" />
          <Input value={form.brand_primary_color || ""} onChange={onField("brand_primary_color")} placeholder="#0A0A0A" className={inputCls} />
        </div>
      </Field>
      <Field label="SECONDARY COLOUR (optional)" testid="brand-secondary" hint="Hex value for secondary accents.">
        <div className="flex items-center gap-2">
          <input type="color" value={form.brand_secondary_color || "#FFC107"} onChange={onField("brand_secondary_color")} data-testid="input-brand-secondary-color" className="h-9 w-12 border border-border cursor-pointer" />
          <Input value={form.brand_secondary_color || ""} onChange={onField("brand_secondary_color")} placeholder="#FFC107" className={inputCls} />
        </div>
      </Field>
      <Field label="SUPPORT EMAIL" testid="support-email" hint="Used as Reply-To on every email we send on your behalf.">
        <Input type="email" value={form.support_email || ""} onChange={onField("support_email")} placeholder="support@yourcompany.example" data-testid="input-support-email" className={inputCls} />
      </Field>
      <Field label="TAGLINE (optional)" testid="brand-tagline" hint="One short line of personality on receipts + emails.">
        <Input value={form.brand_tagline || ""} onChange={onField("brand_tagline")} placeholder="e.g. Always on the road for you." data-testid="input-brand-tagline" className={inputCls} />
      </Field>
    </div>
    <SaveBar busy={busy} testid="branding-save" label="Save branding" onSave={() => onSave({
      public_display_name: form.public_display_name,
      brand_logo_url: form.brand_logo_url,
      brand_primary_color: form.brand_primary_color,
      brand_secondary_color: form.brand_secondary_color,
      support_email: form.support_email, brand_tagline: form.brand_tagline,
    })} />
  </div>
);

// 3 — Booking settings (booking phone · dispatcher phone · contact)
const BookingStep = ({ form, onField, onSave, busy }) => (
  <div className="space-y-3" data-testid="booking-step">
    <p className="text-sm text-muted-foreground">
      The numbers passengers call to book and the desk that handles them. The
      <strong> booking number</strong> is published to customers; the <strong>dispatcher phone</strong>
      is where AI / overflow calls are forwarded to a human.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="PUBLIC BOOKING NUMBER" testid="booking-phone" hint="Shown to customers on your landing page + receipts.">
        <Input value={form.booking_phone || ""} onChange={onField("booking_phone")} placeholder="+44 1256 000 000" data-testid="input-booking-phone" className={inputCls} />
      </Field>
      <Field label="DISPATCHER PHONE" testid="dispatcher-phone" hint="Where AI / overflow calls forward to a human dispatcher.">
        <Input value={form.dispatcher_phone || ""} onChange={onField("dispatcher_phone")} placeholder="+44 1256 000 001" data-testid="input-dispatcher-phone" className={inputCls} />
      </Field>
      <Field label="BOOKING EMAIL / SMS CONTACT" testid="booking-email" hint="Email or SMS contact for booking-related enquiries.">
        <Input type="email" value={form.booking_email || ""} onChange={onField("booking_email")} placeholder="bookings@yourcompany.example" data-testid="input-booking-email" className={inputCls} />
      </Field>
    </div>
    <SaveBar busy={busy} testid="booking-save" label="Save booking settings" onSave={() => onSave({
      booking_phone: form.booking_phone, dispatcher_phone: form.dispatcher_phone,
      booking_email: form.booking_email,
    })} />
  </div>
);

// 4 — Operating area (town · county · radius · centre location)
const AreaStep = ({ form, onField, onSave, busy }) => {
  const applyCountry = (code) => {
    onField("country")(code);
    const c = findCountry(code);
    if (c) {
      onField("currency")(c.currency);
      onField("timezone")(c.timezone);
      onField("date_format")(c.dateFormat);
      onField("time_format")(c.timeFormat);
      onField("addressing_mode")(c.addressingMode);
    }
  };
  const langs = Array.isArray(form.supported_languages) ? form.supported_languages : [];
  const toggleLang = (code) =>
    onField("supported_languages")(langs.includes(code) ? langs.filter((x) => x !== code) : [...langs, code]);
  return (
  <div className="space-y-3" data-testid="area-step">
    <p className="text-sm text-muted-foreground">
      Where you operate. We use the <strong>centre coordinates</strong> to centre the live map and
      bias driver matching towards your area — this is informational, not a hard dispatch boundary.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="MAIN TOWN / CITY" testid="main-town" hint="Your primary service town.">
        <Input value={form.main_town || ""} onChange={onField("main_town")} placeholder="e.g. Basingstoke" data-testid="input-main-town" className={inputCls} />
      </Field>
      <Field label="COUNTY / PROVINCE (optional)" testid="county">
        <Input value={form.county || ""} onChange={onField("county")} placeholder="e.g. Hampshire" data-testid="input-county" className={inputCls} />
      </Field>
      <Field label="OPERATING RADIUS (km)" testid="operating-radius" hint="How far from the centre you'll normally travel.">
        <Input type="number" min={1} max={200} step="0.5" value={form.operating_radius_km ?? ""} onChange={(e) => onField("operating_radius_km")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="25" data-testid="input-operating-radius" className={inputCls} />
      </Field>
    </div>
    <Field label="CENTRE / BASE ADDRESS" testid="base-address" hint="The full street address of your dispatch office or hub.">
      <Input value={form.base_address || ""} onChange={onField("base_address")} placeholder="e.g. 1 Festival Place, Basingstoke RG21 7BB" data-testid="input-base-address" className={inputCls} />
    </Field>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="POSTCODE (optional)" testid="base-postcode">
        <Input value={form.base_postcode || ""} onChange={onField("base_postcode")} placeholder="RG21 7BB" data-testid="input-base-postcode" className={inputCls} />
      </Field>
      <Field label="CENTRE LATITUDE" testid="base-lat" hint="Decimal degrees. Right-click on Google Maps → first number.">
        <Input type="number" step="0.000001" value={form.base_lat ?? ""} onChange={(e) => onField("base_lat")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="51.2667" data-testid="input-base-lat" className={inputCls} />
      </Field>
      <Field label="CENTRE LONGITUDE" testid="base-lng" hint="Decimal degrees. Right-click Google Maps → second number.">
        <Input type="number" step="0.000001" value={form.base_lng ?? ""} onChange={(e) => onField("base_lng")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="-1.0876" data-testid="input-base-lng" className={inputCls} />
      </Field>
    </div>

    {/* Internationalisation (Phase 1) — tenant region + language config. */}
    <div className="border border-border p-4 bg-[#FAFAF7] space-y-3" data-testid="region-language-block">
      <div className="label-mini text-muted-foreground">REGION &amp; LANGUAGE</div>
      <p className="text-[11px] text-muted-foreground">
        Tailors addressing, currency, dates and customer languages to your country — so Dispatch OS works
        the same whether you run in the UK or somewhere postcodes aren't used (e.g. Zimbabwe).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="COUNTRY" testid="country" hint="Sets sensible currency / timezone / addressing defaults.">
          <select className={inputCls} value={form.country || ""} onChange={(e) => applyCountry(e.target.value)} data-testid="select-country">
            <option value="">Select…</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
          </select>
        </Field>
        <Field label="CURRENCY" testid="currency">
          <select className={inputCls} value={form.currency || ""} onChange={onField("currency")} data-testid="select-currency">
            <option value="">Select…</option>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="TIME ZONE" testid="timezone">
          <select className={inputCls} value={form.timezone || ""} onChange={onField("timezone")} data-testid="select-timezone">
            <option value="">Select…</option>
            {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="DATE FORMAT" testid="date-format">
          <select className={inputCls} value={form.date_format || ""} onChange={onField("date_format")} data-testid="select-date-format">
            <option value="">Select…</option>
            {DATE_FORMATS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="TIME FORMAT" testid="time-format">
          <select className={inputCls} value={form.time_format || ""} onChange={onField("time_format")} data-testid="select-time-format">
            <option value="">Select…</option>
            {TIME_FORMATS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="ADDRESSING MODE" testid="addressing-mode" hint="How the AI asks for locations.">
          <select className={inputCls} value={form.addressing_mode || ""} onChange={onField("addressing_mode")} data-testid="select-addressing-mode">
            <option value="">Select…</option>
            {ADDRESSING_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </Field>
      </div>
      {form.addressing_mode && (
        <div className="text-[11px] text-muted-foreground" data-testid="addressing-mode-hint">
          {ADDRESSING_MODES.find((m) => m.value === form.addressing_mode)?.hint}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="DEFAULT LANGUAGE" testid="default-language">
          <select className={inputCls} value={form.default_language || ""} onChange={onField("default_language")} data-testid="select-default-language">
            <option value="">Select…</option>
            {LANGUAGES.filter((l) => l.enabled).map((l) => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
          </select>
        </Field>
      </div>
      <div>
        <Label className="label-mini text-muted-foreground mb-1 block">SUPPORTED CUSTOMER LANGUAGES</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
          {LANGUAGES.map((l) => (
            <label key={l.code} className={`flex items-center gap-2 text-sm ${l.enabled ? "cursor-pointer" : "opacity-60"}`}>
              <input type="checkbox" checked={langs.includes(l.code)} onChange={() => toggleLang(l.code)} data-testid={`lang-${l.code}`} disabled={!l.enabled} />
              <span>{l.flag} {l.name}{!l.enabled && " · soon"}</span>
            </label>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1.5">
          Automatic Voice AI language detection &amp; multilingual replies arrive in a later release
          (Voice AI is currently frozen). Selecting languages now prepares your workspace; more can be
          added over time without code changes.
        </div>
      </div>
    </div>

    <SaveBar busy={busy} testid="area-save" label="Save operating area &amp; region" onSave={() => onSave({
      main_town: form.main_town, county: form.county,
      operating_radius_km: form.operating_radius_km,
      base_address: form.base_address, base_postcode: form.base_postcode,
      base_lat: form.base_lat, base_lng: form.base_lng,
      country: form.country, currency: form.currency, timezone: form.timezone,
      date_format: form.date_format, time_format: form.time_format,
      addressing_mode: form.addressing_mode,
      default_language: form.default_language, supported_languages: form.supported_languages,
    })} />
  </div>
  );
};

// 5 — Pricing (starter rates snapshot; canonical config on Settings tab)
const PricingStep = ({ form, onField, onSave, busy }) => (
  <div className="space-y-3" data-testid="pricing-step">
    <p className="text-sm text-muted-foreground">
      Record your headline rates here for reference, then set the <strong>canonical</strong> pricing
      (VAT, surcharges, and fixed-price airport / hospital / school routes) on the
      <strong> Settings</strong> tab. Tick the box below once your live fares are configured.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="BASE FARE (£)" testid="starter-base-fare" hint="Flat charge applied to every trip.">
        <Input type="number" min={0} step="0.10" value={form.starter_base_fare ?? ""} onChange={(e) => onField("starter_base_fare")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="3.50" data-testid="input-starter-base-fare" className={inputCls} />
      </Field>
      <Field label="PER MILE (£)" testid="starter-per-mile" hint="Distance rate per mile.">
        <Input type="number" min={0} step="0.10" value={form.starter_per_mile ?? ""} onChange={(e) => onField("starter_per_mile")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="2.20" data-testid="input-starter-per-mile" className={inputCls} />
      </Field>
      <Field label="PER MINUTE (£)" testid="starter-per-minute" hint="Time rate per minute.">
        <Input type="number" min={0} step="0.05" value={form.starter_per_minute ?? ""} onChange={(e) => onField("starter_per_minute")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="0.30" data-testid="input-starter-per-minute" className={inputCls} />
      </Field>
      <Field label="WAITING TIME (£/min)" testid="starter-waiting" hint="Charge while waiting at pickup / stops.">
        <Input type="number" min={0} step="0.05" value={form.starter_waiting_per_minute ?? ""} onChange={(e) => onField("starter_waiting_per_minute")(e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="0.40" data-testid="input-starter-waiting" className={inputCls} />
      </Field>
    </div>
    <div className="border border-border p-4 bg-[#FAFAF7]">
      <div className="label-mini text-muted-foreground mb-2">CANONICAL PRICING &amp; FIXED ROUTES</div>
      <div className="flex flex-wrap gap-2">
        <Link to="/admin" state={{ tab: "settings" }} className="text-xs px-3 py-2 border border-border hover:bg-white inline-flex items-center gap-1.5" data-testid="pricing-open-settings">
          <SettingsIcon className="w-3.5 h-3.5" /> Open fare settings
        </Link>
        <Link to="/admin" state={{ tab: "settings", sub: "routes" }} className="text-xs px-3 py-2 border border-border hover:bg-white inline-flex items-center gap-1.5" data-testid="pricing-open-routes">
          <MapPin className="w-3.5 h-3.5" /> Add airport / fixed routes
        </Link>
        <Link to="/admin" state={{ tab: "imports" }} className="text-xs px-3 py-2 border border-border hover:bg-white inline-flex items-center gap-1.5" data-testid="pricing-open-imports">
          <DollarSign className="w-3.5 h-3.5" /> Bulk import fixed routes
        </Link>
      </div>
    </div>
    <SaveBar busy={busy} testid="pricing-save" label="Save starter rates" onSave={() => onSave({
      starter_base_fare: form.starter_base_fare, starter_per_mile: form.starter_per_mile,
      starter_per_minute: form.starter_per_minute, starter_waiting_per_minute: form.starter_waiting_per_minute,
    })} />
    <label className="flex items-center gap-2 cursor-pointer pt-1">
      <input
        type="checkbox"
        checked={!!form.fares_reviewed}
        onChange={(e) => { onField("fares_reviewed")(e.target.checked); onSave({ fares_reviewed: e.target.checked }); }}
        data-testid="input-fares-reviewed"
        disabled={busy}
      />
      <span className="text-sm"><strong>My pricing is set and ready for the live fleet.</strong></span>
    </label>
  </div>
);

// 6 — Drivers
const DriversStep = ({ counts, progress }) => (
  <div className="space-y-3" data-testid="drivers-step">
    <p className="text-sm text-muted-foreground">
      Add your first driver to unlock dispatching. From there, our document
      review + approval flow handles the rest of the compliance work for you —
      no driver can take a ride until they're approved.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Tile label="Drivers in fleet" value={counts.drivers} testid="drivers-count" tone={counts.drivers > 0 ? undefined : "warn"} />
      <Tile label="Currently online" value={counts.drivers_online} testid="drivers-online-count" />
      <Tile label="Step status" value={progress.drivers.done ? "Complete" : "Add a driver"} testid="drivers-step-status" tone={progress.drivers.done ? "good" : "warn"} />
    </div>
    <div className="border border-border p-4 bg-[#FAFAF7] space-y-2">
      <div className="label-mini text-muted-foreground">CHECKLIST</div>
      <ChecklistItem label="Add one driver manually (Fleet → Drivers → Add driver) OR bulk-import via CSV." />
      <ChecklistItem label="Enter each driver's vehicle details (make, model, plate, seats)." />
      <ChecklistItem label="Review every pending application in Approvals — drivers stay blocked until you tick them off." />
      <ChecklistItem label="Verify each driver's licence, badge, right to work, DBS, photo, MOT, insurance, private hire vehicle licence, and V5C logbook are uploaded and approved." />
      <div className="flex flex-wrap gap-2 pt-1">
        <Link to="/admin" state={{ tab: "fleet", sub: "drivers" }} className="text-xs px-3 py-2 border border-border hover:bg-white inline-flex items-center gap-1.5" data-testid="drivers-open-fleet">
          <Users className="w-3.5 h-3.5" /> Open Fleet → Drivers
        </Link>
        <Link to="/admin" state={{ tab: "approvals" }} className="text-xs px-3 py-2 border border-border hover:bg-white inline-flex items-center gap-1.5" data-testid="drivers-open-approvals">
          <UserCheck className="w-3.5 h-3.5" /> Open Approvals
        </Link>
        <Link to="/admin" state={{ tab: "imports" }} className="text-xs px-3 py-2 border border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-black inline-flex items-center gap-1.5" data-testid="drivers-open-imports">
          <Users className="w-3.5 h-3.5" /> Bulk import drivers
        </Link>
      </div>
    </div>
  </div>
);

// 7 — Payments (accepted methods · Stripe status)
const PAYMENT_METHODS = [
  { id: "cash", label: "Cash" },
  { id: "card", label: "Card (in-car / online)" },
  { id: "account", label: "Account / corporate invoice" },
];

const PaymentsStep = ({ form, onField, onSave, busy }) => {
  const methods = Array.isArray(form.payment_methods) ? form.payment_methods : [];
  const toggle = (m) =>
    onField("payment_methods")(methods.includes(m) ? methods.filter((x) => x !== m) : [...methods, m]);
  const stripeOn = methods.includes("card");
  return (
    <div className="space-y-3" data-testid="payments-step">
      <p className="text-sm text-muted-foreground">
        Choose how customers pay. This is a display/settings snapshot — the actual card
        processing continues to run through your existing Stripe configuration on the
        <strong> Settings</strong> tab.
      </p>
      <div className="border border-border p-4 bg-[#FAFAF7] space-y-2">
        <div className="label-mini text-muted-foreground mb-1">ACCEPTED PAYMENT METHODS</div>
        {PAYMENT_METHODS.map((pm) => (
          <label key={pm.id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
            <input
              type="checkbox"
              checked={methods.includes(pm.id)}
              onChange={() => toggle(pm.id)}
              data-testid={`payment-method-${pm.id}`}
            />
            <span>{pm.label}</span>
          </label>
        ))}
      </div>
      <div
        className={`border p-3 text-xs flex items-start gap-2 ${stripeOn ? "border-[#10B981] bg-[#ECFDF5] text-[#065F46]" : "border-border bg-white text-muted-foreground"}`}
        data-testid="payments-stripe-status"
      >
        <CreditCard className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          <strong>Stripe {stripeOn ? "enabled" : "off"}.</strong>{" "}
          {stripeOn
            ? "Card payments are accepted. Confirm your live Stripe keys are set on the Settings tab before going live."
            : "Card payments are currently off. Enable \"Card\" above and configure Stripe on the Settings tab to accept card payments."}{" "}
          <Link to="/admin" state={{ tab: "settings", sub: "payments" }} className="underline" data-testid="payments-open-settings">Open payment settings</Link>
        </span>
      </div>
      <SaveBar busy={busy} testid="payments-save" label="Save payment methods" onSave={() => onSave({ payment_methods: methods })} />
    </div>
  );
};

// 8 — AI settings (enable/disable · fallback phone · local aliases)
const AiStep = ({ form, onField, onSave, busy }) => {
  const enabled = form.ai_booking_enabled;
  return (
    <div className="space-y-3" data-testid="ai-step">
      <p className="text-sm text-muted-foreground">
        The AI booking line answers calls and takes bookings in natural language. Choose whether
        to enable it, where unclear calls should fall back to a human, and any
        <strong> local landmarks / aliases</strong> the AI should recognise.
      </p>
      <div className="border border-border p-4 bg-[#FAFAF7]">
        <div className="label-mini text-muted-foreground mb-2">AI BOOKING</div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => onField("ai_booking_enabled")(true)}
            className={`text-xs ${enabled === true ? "bg-[#0A0A0A] text-white hover:bg-black" : "bg-white text-[#0A0A0A] border border-border hover:bg-secondary"}`}
            data-testid="ai-enable-btn"
          >
            Enable AI booking
          </Button>
          <Button
            type="button"
            onClick={() => onField("ai_booking_enabled")(false)}
            className={`text-xs ${enabled === false ? "bg-[#0A0A0A] text-white hover:bg-black" : "bg-white text-[#0A0A0A] border border-border hover:bg-secondary"}`}
            data-testid="ai-disable-btn"
          >
            Disable AI booking
          </Button>
        </div>
        {enabled === undefined || enabled === null ? (
          <div className="text-[11px] text-[#92400E] mt-2">Make a choice to complete this step.</div>
        ) : (
          <div className="text-[11px] text-muted-foreground mt-2">
            AI booking is currently <strong>{enabled ? "enabled" : "disabled"}</strong>.
          </div>
        )}
      </div>
      <Field label="VOICE AI FALLBACK PHONE" testid="ai-fallback-phone" hint="Where the AI transfers callers it can't help (usually your dispatcher).">
        <Input value={form.ai_fallback_phone || ""} onChange={onField("ai_fallback_phone")} placeholder="+44 1256 000 001" data-testid="input-ai-fallback-phone" className={inputCls} />
      </Field>
      <Field label="LOCAL ALIASES / LANDMARKS (optional)" testid="local-aliases" hint="Comma or line separated — helps the AI recognise local places (e.g. 'The Malls = Festival Place').">
        <textarea
          value={form.local_aliases || ""} onChange={onField("local_aliases")}
          rows={4} maxLength={2000}
          placeholder={"The Malls = Festival Place\nThe hospital = Basingstoke & North Hampshire Hospital, RG24 9NA"}
          data-testid="input-local-aliases" className={`${inputCls} resize-none`}
        />
      </Field>
      <SaveBar busy={busy} testid="ai-save" label="Save AI settings" onSave={() => onSave({
        ai_booking_enabled: typeof form.ai_booking_enabled === "boolean" ? form.ai_booking_enabled : null,
        ai_fallback_phone: form.ai_fallback_phone, local_aliases: form.local_aliases,
      })} />
    </div>
  );
};

// 9 — Test booking
const TestBookingStep = ({ form, onField, onSave, busy }) => (
  <div className="space-y-3" data-testid="test-step">
    <p className="text-sm text-muted-foreground">
      Before you go live, run <strong>one manual test booking</strong> end-to-end to prove the
      whole chain works: a job is created, an online driver receives the offer, and the
      confirmation SMS is delivered.
    </p>
    <div className="border border-border p-4 bg-[#FAFAF7] space-y-2">
      <div className="label-mini text-muted-foreground">TEST CHECKLIST</div>
      <ChecklistItem label="Create a manual booking (Dispatch → New booking) with a real pickup + drop-off." />
      <ChecklistItem label="Confirm an online driver receives the job offer in their cockpit." />
      <ChecklistItem label="Confirm the booking-confirmation SMS is received on the test phone." />
      <div className="flex flex-wrap gap-2 pt-1">
        <Link to="/admin" state={{ tab: "dispatch" }} className="text-xs px-3 py-2 border border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-black inline-flex items-center gap-1.5" data-testid="test-open-dispatch">
          <ClipboardCheck className="w-3.5 h-3.5" /> Open Dispatch → New booking
        </Link>
      </div>
    </div>
    <label className="flex items-center gap-2 cursor-pointer pt-1">
      <input
        type="checkbox"
        checked={!!form.test_booking_confirmed}
        onChange={(e) => { onField("test_booking_confirmed")(e.target.checked); onSave({ test_booking_confirmed: e.target.checked }); }}
        data-testid="input-test-booking-confirmed"
        disabled={busy}
      />
      <span className="text-sm"><strong>I ran a test booking — the driver received the job and the SMS worked.</strong></span>
    </label>
  </div>
);

// 10 — Go-live checklist
const GoLiveStep = ({ summary, progress, onGoLive, busy }) => {
  const items = STEP_META.filter((s) => s.id !== "golive");
  const allDone = items.every((s) => progress[s.id]?.done);
  return (
    <div className="space-y-3" data-testid="golive-step">
      <p className="text-sm text-muted-foreground">
        The final check. Every setup step below must be complete before you can mark your
        operator ready to operate. Once you go live, you can keep editing any setting at any time.
      </p>
      <div className="border border-border divide-y divide-border" data-testid="golive-readiness-list">
        {items.map((s) => {
          const done = progress[s.id]?.done;
          return (
            <div key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`golive-item-${s.id}`}>
              {done
                ? <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                : <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
              <span className="flex-1">{s.label}</span>
              <span className={`label-mini ${done ? "text-[#065F46]" : "text-[#92400E]"}`}>{done ? "DONE" : "TO DO"}</span>
            </div>
          );
        })}
      </div>

      {summary.go_live_confirmed ? (
        <div className="border border-[#10B981] bg-[#ECFDF5] text-[#065F46] px-3 py-2 text-sm flex items-start gap-2" data-testid="golive-confirmed">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span><strong>You're live.</strong> Your operator is marked ready to operate.</span>
        </div>
      ) : (
        <div className="pt-1">
          <Button
            onClick={onGoLive}
            disabled={busy || !allDone}
            className="bg-[#0A0A0A] hover:bg-black text-white text-xs font-bold flex items-center gap-1.5"
            data-testid="golive-confirm-btn"
          >
            <Rocket className="w-3.5 h-3.5" /> Mark operator ready to operate
          </Button>
          {!allDone && (
            <div className="text-[11px] text-muted-foreground mt-1">
              Finish every step above ({summary.steps_done}/{summary.steps_total} done) to unlock go-live.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Tile = ({ label, value, testid, tone }) => {
  const cls = tone === "good"
    ? "border-[#10B981] bg-[#ECFDF5] text-[#065F46]"
    : tone === "warn"
      ? "border-[#F59E0B] bg-[#FFFBEB] text-[#92400E]"
      : "border-border bg-white";
  return (
    <div className={`border p-3 ${cls}`} data-testid={`tile-${testid}`}>
      <div className="label-mini opacity-70">{label}</div>
      <div className="text-xl font-heading font-black mt-0.5" data-testid={`tile-${testid}-value`}>{value}</div>
    </div>
  );
};

const ChecklistItem = ({ label }) => (
  <div className="flex items-start gap-2 text-sm">
    <Circle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
    <span>{label}</span>
  </div>
);

export default OnboardingWizard;
